import { PaymentElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { getAuth } from 'firebase/auth';
import { useEffect, useMemo, useState } from 'react';
import { app } from '../firebase';
import { ANALYTICS_EVENTS, buildPurchaseAnalyticsParams, trackAnalyticsEvent } from '../services/analytics';
import { createLivePaymentIntent } from '../services/payments';
import './Checkout.css';

const auth = getAuth(app);
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

const parsePrice = (price) => {
    const numericPrice = Number(String(price || 0).replace(/[^0-9.]/g, ''));
    return Number.isFinite(numericPrice) ? numericPrice : 0;
};

const PLATFORM_FEE_RATE = 0.05;

const centsFromPrice = (price) => Math.max(0, Math.round(parsePrice(price) * 100));

const formatCents = (cents) => `$${(Number(cents || 0) / 100).toFixed(2)}`;

const getCheckoutTotals = (price) => {
    const beatLicenseCents = centsFromPrice(price);
    const buyerPlatformFeeCents = 0;
    const platformCommissionCents = Math.max(0, Math.round(beatLicenseCents * PLATFORM_FEE_RATE));
    const producerPayoutCents = Math.max(0, beatLicenseCents - platformCommissionCents);

    return {
        beatLicenseCents,
        buyerPlatformFeeCents,
        platformCommissionCents,
        producerPayoutCents,
        totalChargedCents: beatLicenseCents + buyerPlatformFeeCents,
    };
};

const trackPaymentFailure = (track, paymentIntentId, source, failureReason) => {
    trackAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_FAILURE, buildPurchaseAnalyticsParams(track, paymentIntentId, {
        source,
        failure_reason: failureReason,
    }));
};

const PaymentCheckoutForm = ({ track, paymentIntentId, source, onSuccess, onCancel }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const totals = getCheckoutTotals(track.price);

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!stripe || !elements) {
            setError('Secure payment form is still loading. Please try again in a moment.');
            trackPaymentFailure(track, paymentIntentId, source, 'payment_form_unavailable');
            return;
        }

        setSubmitting(true);
        setError('');

        const { error: submitError } = await elements.submit();
        if (submitError) {
            setError(submitError.message || 'Please check your payment details.');
            setSubmitting(false);
            trackPaymentFailure(track, paymentIntentId, source, submitError.code || 'payment_details_invalid');
            return;
        }

        const { error: paymentError, paymentIntent } = await stripe.confirmPayment({
            elements,
            redirect: 'if_required',
            confirmParams: {
                return_url: `${window.location.origin}/library`,
            },
        });

        if (paymentError) {
            setError(paymentError.message || 'Payment could not be completed. Please try again.');
            setSubmitting(false);
            trackPaymentFailure(track, paymentIntentId, source, paymentError.code || 'payment_confirm_failed');
            return;
        }

        if (paymentIntent?.status === 'succeeded' || paymentIntent?.status === 'processing') {
            trackAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_SUCCESS, buildPurchaseAnalyticsParams(track, paymentIntent.id || paymentIntentId, {
                source,
                payment_status: paymentIntent.status,
            }));
            onSuccess(paymentIntent.id || paymentIntentId);
            return;
        }

        setError('Payment was not completed. Please try again or use a different payment method.');
        setSubmitting(false);
        trackPaymentFailure(track, paymentIntent?.id || paymentIntentId, source, paymentIntent?.status || 'payment_not_completed');
    };

    return (
        <form onSubmit={handleSubmit} className="checkout-form">
            <div className="checkout-header">
                <h2>License Beat</h2>
                <button type="button" className="close-btn" onClick={onCancel} disabled={submitting}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                </button>
            </div>

            <div className="checkout-track-info">
                <h3>{track.title || 'Untitled'}</h3>
                <p>by {track.artist || 'Unknown Artist'}</p>
                <div className="checkout-price">
                    <span className="price-label">Total today</span>
                    <span className="price-value">{formatCents(totals.totalChargedCents)}</span>
                </div>
            </div>

            <div className="checkout-breakdown">
                <div className="breakdown-item">
                    <span>Beat license</span>
                    <span>{formatCents(totals.beatLicenseCents)}</span>
                </div>
                <div className="breakdown-item">
                    <span>Buyer platform fee</span>
                    <span>{formatCents(totals.buyerPlatformFeeCents)}</span>
                </div>
                <div className="breakdown-item">
                    <span>Tune fee (5%)</span>
                    <span>{formatCents(totals.platformCommissionCents)}</span>
                </div>
                <div className="breakdown-item">
                    <span>Producer balance</span>
                    <span>{formatCents(totals.producerPayoutCents)}</span>
                </div>
                <div className="breakdown-item total">
                    <span>Total charged</span>
                    <span>{formatCents(totals.totalChargedCents)}</span>
                </div>
            </div>

            <div className="payment-section live-payment-section">
                <h3>Secure payment</h3>
                <PaymentElement />
            </div>

            <div className="checkout-live-note">
                <strong>What happens next</strong>
                <p>After payment succeeds, Tune adds the license to your Library and credits the producer balance shown above.</p>
                <span>No buyer platform fee. Tune keeps 5% from the listed beat price.</span>
            </div>

            {error && (
                <div className="checkout-error">
                    {error}
                </div>
            )}

            <div className="checkout-actions">
                <button
                    type="button"
                    className="cancel-btn"
                    onClick={onCancel}
                    disabled={submitting}
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    className="purchase-btn"
                    disabled={!stripe || !elements || submitting}
                >
                    {submitting ? (
                        <>
                            <span className="spinner"></span>
                            Processing...
                        </>
                    ) : (
                        `Pay ${formatCents(totals.totalChargedCents)}`
                    )}
                </button>
            </div>

            <div className="checkout-footer">
                <p>Payments are processed securely by Stripe. Tune never stores card details.</p>
            </div>
        </form>
    );
};

const CheckoutLoader = ({ track, source, onSuccess, onCancel }) => {
    const [clientSecret, setClientSecret] = useState('');
    const [paymentIntentId, setPaymentIntentId] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    const trackPriceCents = centsFromPrice(track.price);

    useEffect(() => {
        let active = true;

        const preparePayment = async () => {
            const user = auth?.currentUser;
            if (!user) {
                setError('Please log in to purchase this beat.');
                setLoading(false);
                trackPaymentFailure(track, '', source, 'auth_required');
                return;
            }

            if (track.userId === user.uid) {
                setError('You cannot purchase your own beat.');
                setLoading(false);
                trackPaymentFailure(track, '', source, 'own_track');
                return;
            }

            if (!stripePromise) {
                setError('Stripe is not configured yet. Add REACT_APP_STRIPE_PUBLISHABLE_KEY before enabling live checkout.');
                setLoading(false);
                trackPaymentFailure(track, '', source, 'stripe_not_configured');
                return;
            }

            if (!track.id) {
                setError('This beat is missing its track ID.');
                setLoading(false);
                trackPaymentFailure(track, '', source, 'missing_track_id');
                return;
            }

            if (trackPriceCents < 50) {
                setError('Live checkout requires a price of at least $0.50.');
                setLoading(false);
                trackPaymentFailure(track, '', source, 'price_too_low');
                return;
            }

            try {
                setLoading(true);
                setError('');
                const intent = await createLivePaymentIntent({
                    trackId: track.id,
                    licenseType: track.licenseType || 'standard',
                });

                if (!active) return;
                setClientSecret(intent.clientSecret || '');
                setPaymentIntentId(intent.paymentIntentId || '');
            } catch (err) {
                if (!active) return;
                setError(err.message || 'Could not start checkout. Please try again.');
                trackPaymentFailure(track, '', source, err.code || 'payment_intent_failed');
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        preparePayment();

        return () => {
            active = false;
        };
    }, [source, track, track.id, track.licenseType, track.userId, trackPriceCents]);

    const elementsOptions = useMemo(() => ({
        clientSecret,
        appearance: {
            theme: 'night',
            variables: {
                colorPrimary: '#c98516',
                colorBackground: '#15100a',
                colorText: '#fff8ea',
                colorDanger: '#fca5a5',
                borderRadius: '8px',
                fontFamily: 'Inter, system-ui, sans-serif',
            },
        },
    }), [clientSecret]);

    if (loading) {
        return (
            <div className="checkout-form checkout-loading-state">
                <div className="checkout-header">
                    <h2>Preparing checkout</h2>
                    <button type="button" className="close-btn" onClick={onCancel}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"/>
                            <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>
                <div className="checkout-loading-card">
                    <span className="spinner"></span>
                    <p>Creating a secure Stripe payment...</p>
                </div>
            </div>
        );
    }

    if (error || !clientSecret) {
        return (
            <div className="checkout-form checkout-loading-state">
                <div className="checkout-header">
                    <h2>Checkout unavailable</h2>
                    <button type="button" className="close-btn" onClick={onCancel}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"/>
                            <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>
                <div className="checkout-error checkout-setup-error">
                    {error || 'Could not create a secure checkout session.'}
                </div>
                <div className="checkout-actions">
                    <button type="button" className="cancel-btn" onClick={onCancel}>Close</button>
                </div>
            </div>
        );
    }

    return (
        <Elements stripe={stripePromise} options={elementsOptions}>
            <PaymentCheckoutForm
                track={track}
                paymentIntentId={paymentIntentId}
                source={source}
                onSuccess={onSuccess}
                onCancel={onCancel}
            />
        </Elements>
    );
};

const Checkout = ({ track, source = 'checkout', onSuccess, onCancel }) => {
    useEffect(() => {
        if (!track) return;

        trackAnalyticsEvent(ANALYTICS_EVENTS.CHECKOUT_OPENED, buildPurchaseAnalyticsParams(track, '', {
            source,
        }));
    }, [source, track]);

    if (!track) return null;

    return (
        <div className="checkout-overlay" onClick={onCancel}>
            <div className="checkout-modal" onClick={(event) => event.stopPropagation()}>
                <CheckoutLoader track={track} source={source} onSuccess={onSuccess} onCancel={onCancel} />
            </div>
        </div>
    );
};

export default Checkout;
